// Deliberate duplicate of town-square/src/lib/arty-actions/catalog.ts (ADR 0002). A change here belongs there too.
// Client divergences: art review and mockups open in a new tab (both need a Clerk session and
// this app is cross-site to them, so an embed would show their sign-in page), so `review` and
// `mockups` carry the "Opens app" badge; and the per-piece URLs come straight from `@arthelper/shared/artist-standard`
// (no NEXT_PUBLIC_* overrides here).
import {
  Award,
  BarChart3,
  BookOpen,
  DollarSign,
  Eye,
  ExternalLink,
  FileText,
  Heading,
  ImageIcon,
  Landmark,
  MessageCircle,
  Pencil,
  ScanEye,
  Send,
  Share2,
  Sparkles,
  Tag,
  Users,
  type LucideIcon,
} from 'lucide-react';

import { artReviewUrlFor, mockupsUrlFor } from '@arthelper/shared/artist-standard';
import { isKnownActionId, librarySparkIdFor } from '@arthelper/shared/arty-pins';
import { ACTION_IDS, type ActionId } from './ids';

/**
 * Everything the artist can do with one piece, in one list - the card's pinned
 * shortcuts and the "everything you can do" sheet render the same rows.
 *
 * `kind` is the analytics bucket (`spark` sends a natural sentence into the
 * chat, with the spark's CompletionType id riding the turn; `link` opens an
 * ArtHelper product; `share` opens the share sheet; `community` opens the post
 * modal). Which surface a tap opens is decided per id by the card's dispatcher.
 *
 * Copy lives in the `arty-card` catalog under `actions.<id>` (`label`,
 * `description`, and `message` for sparks); this module holds structure only.
 */
export type ActionGroup = 'products' | 'sell' | 'communityFeedback' | 'words' | 'improve' | 'sparks';

export type ActionKind = 'spark' | 'link' | 'share' | 'community';

export type CommunityKind = 'critique' | 'rating' | 'pricing' | 'poll';

export type Translate = (key: string, values?: Record<string, any>) => string;

/** Section order in the sheet. Products lead, with their previews. */
export const GROUP_ORDER: ActionGroup[] = ['products', 'sell', 'communityFeedback', 'words', 'improve', 'sparks'];

export type ArtyAction = {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  group: ActionGroup;
  kind: ActionKind;
  message?: string;
  /** Spark tiles only: CompletionType names to match, in order. */
  completionTypeNames?: readonly string[];
  communityKind?: CommunityKind;
  /** Leaves for another app: the tile says so. */
  opensApp?: boolean;
  /**
   * Bind to a CompletionType the account has switched off. A disabled row
   * means "not in the artist's spark menu", not "unrunnable" - the API runs it
   * either way, and this tile is Arty's own entry point into that spark.
   */
  allowDisabled?: boolean;
  /** Resolved CompletionType id, when one matched. */
  completionTypeId?: string;
};

type CatalogEntry = Pick<
  ArtyAction,
  'icon' | 'group' | 'kind' | 'completionTypeNames' | 'communityKind' | 'opensApp' | 'allowDisabled'
> & { id: ActionId };

/** Catalog order is display order inside a group: review, mockups, then the book. */
export const CATALOG: readonly CatalogEntry[] = [
  { id: 'review', icon: ScanEye, group: 'products', kind: 'link', opensApp: true },
  { id: 'mockups', icon: ImageIcon, group: 'products', kind: 'link', opensApp: true },
  { id: 'artbook', icon: BookOpen, group: 'products', kind: 'link', opensApp: true },
  { id: 'price', icon: Tag, group: 'sell', kind: 'spark', completionTypeNames: ['Price My Art', 'Pricing'] },
  { id: 'collector', icon: Users, group: 'sell', kind: 'spark', completionTypeNames: ['Ideal Collector'] },
  {
    id: 'galleries',
    icon: Landmark,
    group: 'sell',
    kind: 'spark',
    completionTypeNames: ['Gallery matches', 'Gallery Matches'],
  },
  { id: 'share', icon: Share2, group: 'sell', kind: 'share' },
  { id: 'critique', icon: MessageCircle, group: 'communityFeedback', kind: 'community', communityKind: 'critique' },
  { id: 'rating', icon: Award, group: 'communityFeedback', kind: 'community', communityKind: 'rating' },
  { id: 'pricing', icon: DollarSign, group: 'communityFeedback', kind: 'community', communityKind: 'pricing' },
  { id: 'poll', icon: BarChart3, group: 'communityFeedback', kind: 'community', communityKind: 'poll' },
  { id: 'description', icon: FileText, group: 'words', kind: 'spark', completionTypeNames: ['Product Description'] },
  { id: 'title', icon: Heading, group: 'words', kind: 'spark', completionTypeNames: ['Product Title', 'Etsy Title'] },
  {
    id: 'social',
    icon: Send,
    group: 'words',
    kind: 'spark',
    completionTypeNames: ['Social Post', 'Social Media Post'],
  },
  {
    id: 'artyTake',
    icon: Eye,
    group: 'improve',
    kind: 'spark',
    // Arty's take IS the "Art Analysis" spark (CompletionType 26), the holistic
    // single-piece critique. Production switched that row off when the Art
    // Review app launched, and this tile is what keeps it reachable.
    allowDisabled: true,
    completionTypeNames: ['Art Analysis'],
  },
  { id: 'edit', icon: Pencil, group: 'improve', kind: 'link' },
  { id: 'view', icon: ExternalLink, group: 'improve', kind: 'link', opensApp: true },
];

/** Guards the hand-written id union against the catalog. */
export const CATALOG_IDS: readonly string[] = ACTION_IDS;

/** One `completionTypes` row, as the viewer query returns it. */
export type CompletionTypeRow = {
  id: string;
  name: string;
  description?: string | null;
  enabled?: boolean | null;
  expiresAt?: string | null;
  isLinkOut?: boolean | null;
};

/**
 * Rows a tile may bind to. `enabled` is deliberately not a filter here: a row
 * the account switched off still runs. Expiry and link-out are different - an
 * expired spark errors, and a link-out row is a URL, not a completion.
 */
export function bindableCompletionTypes(
  rows: readonly CompletionTypeRow[] | undefined | null,
  now: number = Date.now()
): CompletionTypeRow[] {
  return (rows ?? []).filter((row) => {
    if (row.isLinkOut === true) {
      return false;
    }
    if (!row.expiresAt) {
      return true;
    }
    const expiry = Date.parse(row.expiresAt);
    return Number.isNaN(expiry) || expiry > now;
  });
}

/** The artist-visible set: bindable rows the account has switched on. */
export function usableCompletionTypes(
  rows: readonly CompletionTypeRow[] | undefined | null,
  now: number = Date.now()
): CompletionTypeRow[] {
  return bindableCompletionTypes(rows, now).filter((row) => {
    return row.enabled === true;
  });
}

function normalizeName(name: string): string {
  return name.trim().toLowerCase();
}

export function librarySparkId(name: string): string {
  return librarySparkIdFor(name);
}

/** First case-insensitive name hit wins, in the order the catalog lists. */
function matchCompletionType(
  names: readonly string[] | undefined,
  rows: readonly CompletionTypeRow[]
): CompletionTypeRow | undefined {
  return (names ?? []).reduce<CompletionTypeRow | undefined>((memo, name) => {
    if (memo) {
      return memo;
    }
    return rows.find((row) => {
      return normalizeName(row.name) === normalizeName(name);
    });
  }, undefined);
}

function curatedAction(entry: CatalogEntry, t: Translate): ArtyAction {
  const prefix = `actions.${entry.id}`;
  const action: ArtyAction = {
    ...entry,
    label: t(`${prefix}.label`),
    description: t(`${prefix}.description`),
  };
  if (entry.kind !== 'spark') {
    return action;
  }
  return { ...action, message: t(`${prefix}.message`) };
}

/** A row the artist enabled that is not already a curated tile. */
export function librarySparkActions(rows: readonly CompletionTypeRow[], t: Translate): ArtyAction[] {
  const curatedNames = new Set(
    CATALOG.flatMap((entry) => {
      return (entry.completionTypeNames ?? []).map(normalizeName);
    })
  );
  return rows.reduce<ArtyAction[]>((memo, row) => {
    const name = row.name?.trim();
    if (!name || curatedNames.has(normalizeName(name))) {
      return memo;
    }
    const id = librarySparkId(name);
    const seen = memo.some((action) => {
      return action.id === id;
    });
    if (seen) {
      return memo;
    }
    const action: ArtyAction = {
      id,
      label: name,
      description: row.description?.trim() || t('library.description'),
      icon: Sparkles,
      group: 'sparks',
      kind: 'spark',
      message: t('library.message', { name }),
      completionTypeId: row.id,
    };
    return [...memo, action];
  }, []);
}

/** Curated tiles with CompletionType ids filled in, then the artist's own sparks. */
export function resolveActions(
  rows: readonly CompletionTypeRow[] | undefined | null,
  t: Translate,
  now: number = Date.now()
): ArtyAction[] {
  const usable = usableCompletionTypes(rows, now);
  const bindable = bindableCompletionTypes(rows, now);
  const curated = CATALOG.map((entry) => {
    const action = curatedAction(entry, t);
    const match = matchCompletionType(entry.completionTypeNames, entry.allowDisabled ? bindable : usable);
    if (!match) {
      return action;
    }
    return { ...action, completionTypeId: match.id };
  });
  return [...curated, ...librarySparkActions(usable, t)];
}

export function findAction(actions: readonly ArtyAction[], id: string): ArtyAction | undefined {
  return actions.find((action) => {
    return action.id === id;
  });
}

/** Pins in pin order, stale ids dropped: the star order is the card's order. */
export function pinnedActions(actions: readonly ArtyAction[], pins: readonly string[]): ArtyAction[] {
  return pins.reduce<ArtyAction[]>((memo, id) => {
    const action = findAction(actions, id);
    if (!action) {
      return memo;
    }
    return [...memo, action];
  }, []);
}

/**
 * The pins that still hold a slot, in pin order. A pin with no tile is dead only once the spark
 * library has loaded (`sparksReady`): until then a `spark:*` pin may simply not have arrived yet,
 * and treating it as dead would delete it on the next star tap.
 */
export function livePins(actions: readonly ArtyAction[], pins: readonly string[], sparksReady: boolean): string[] {
  return pins.filter((id) => {
    if (findAction(actions, id)) {
      return true;
    }
    if (sparksReady) {
      return false;
    }
    return isKnownActionId(id);
  });
}

/** A curated spark whose CompletionType the account does not have. */
export function isUnavailableSpark(action: ArtyAction): boolean {
  return action.kind === 'spark' && !action.completionTypeId;
}

export type ActionSection = { group: ActionGroup; label: string; actions: ArtyAction[] };

/** Sections for the sheet, in group order, empty ones dropped. */
export function groupedActions(actions: readonly ArtyAction[], t: Translate): ActionSection[] {
  return GROUP_ORDER.flatMap((group) => {
    const rows = actions.filter((action) => {
      return action.group === group;
    });
    if (rows.length === 0) {
      return [];
    }
    return [{ group, label: t(`groups.${group}`), actions: rows }];
  });
}

/** Search across what the tile shows. */
export function matchesQuery(action: ArtyAction, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return action.label.toLowerCase().includes(needle) || action.description.toLowerCase().includes(needle);
}

export function isProcessed(status: string | null | undefined): boolean {
  return !status || status === 'processed';
}

/** Art Review on this piece. */
export function reviewUrl(imageId: string): string {
  return artReviewUrlFor(imageId);
}

/** The art book is built per piece inside Art Review. */
export function artbookUrl(imageId: string): string {
  return `${reviewUrl(imageId)}#artbook`;
}

export function mockupsUrl(imageId: string): string {
  return mockupsUrlFor(imageId);
}

const SHARE_UTM = 'utm_source=arthelper&utm_term=arty_imagebox';

export function shareUrl(publicUrl: string): string {
  return `${publicUrl}?${SHARE_UTM}`;
}
